/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import './MedliSearchResult.scss';

import MedicalTermsModel from '../../../../models/MedicalTermsModel';
import { Small } from '../../../components/Text';
import IconInfo from '../../../components/icons/IconInfo';
import { TextLink } from '../../../components/TextLink';
import { Grid } from '../../../components/Grid';
import ClickableIcon from '../../../components/ClickableIcon';
import { Icons } from '../../../../bphComponents/bundle';
import FlexRow from '../../../components/FlexRow';
import COLORS from '../../../styles/Colors';

interface Props {
  searchResult: MedicalTermsModel;
  openLongDefinition(sr): void;
  isLastResult: Boolean;
}

const MedliSearchResult: React.FC<Props> = (props) => {
  const { isLastResult, searchResult, openLongDefinition } = props;

  const shortDefinitionRef = React.useRef(null);

  const [state, setState] = React.useState({
    showingShortDefinition: false,
    showingLongDefinition: false,
    showingMoreButton: false,
    longDefinition: '',
  });

  React.useEffect(() => {
    if (state.showingShortDefinition && isLastResult) {
      shortDefinitionRef.current.scrollIntoView(false);
    }
  }, []);

  return (
    <div className="cmp-medli-searchresult" onClick={state.showingLongDefinition ? (event) => { event.stopPropagation(); } : null}>
      <div className="cmp-medli-searchresult-heading">
        <FlexRow style={{ flexWrap: 'nowrap' }}>
          <div>
              <div className="cmp-medli-searchresult-name">
                {searchResult.name()}
              </div>
              {
                searchResult.hasCanonical() ? (
                  <div className="cmp-medli-searchresult-canonical">
                    {searchResult.canonicalName()}
                  </div>
                ) : null
              }
            </div>

            {
              searchResult.hasShortDefinition() ? (
                <div style={{ position: 'relative', width: 56 }}>
                  <div style={{ position: 'absolute', top: -20 }}>
                    <ClickableIcon
                      align="right"
                      onClick={(e) => {
                        e.stopPropagation();
                        setState({
                          ...state,
                          showingShortDefinition: !state.showingShortDefinition,
                        });
                      }}
                    >
                      <IconInfo />
                    </ClickableIcon>
                  </div>
                </div>
              ) : null
            }
        </FlexRow>
        {
          searchResult.isTrackable() ? (
            <div className="font-caption-1 font-color-gsMedDark" style={{ display: 'flex', alignItems: 'center' }}>
              <FormattedMessage id="search.relatedTrackable.label" />
              <div style={{ marginLeft: 4, display: 'flex', alignItems: 'center' }}>
                <Icons.IconTracking.Small color={COLORS.gsMedDark} />
              </div>
            </div>
          ) : null
        }
      </div>
      {
        state.showingShortDefinition ? (
          <div ref={shortDefinitionRef} className="cmp-medlisearchresult-shortdefinition">
            <div className="cmp-medlisearchresult-shortdefinition-text">
              {searchResult.shortDefinition()}

              {
                searchResult.hasShortDefinitionSource() ? (
                  <span className="cmp-medlisearchresult-source-text"> <FormattedMessage id="medicalTermDef.source.field.label" values={{ string1: searchResult.shortDefinitionSource() }} /></span>
                ) : null
              }
            </div>
            <div>
              {
                searchResult.hasLongDefinition() ? (
                  <Grid justifyContent="flex-end">
                    <TextLink
                      className="more-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        openLongDefinition(searchResult);
                      }}
                    >
                      <Small color="profile">
                        <FormattedMessage id="medicalTermDef.more.button.label" />
                      </Small>
                    </TextLink>
                  </Grid>
                ) : null
              }
            </div>
          </div>
        ) : null
      }
    </div>
  );
};

export default MedliSearchResult;
